import type { ReactNode } from "react";

/**
 * The form vocabulary every product screen shares.
 *
 * Lifted out of `routes/products.tsx` when the create form arrived. Two screens
 * that edit the same record have to look identical, and the way that stops
 * being true is one of them growing its own copy of a class string — the same
 * reasoning as arrow-fe's `auth/dialog-parts.tsx`.
 */

export const input =
  "w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] text-ink placeholder:text-faint";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="mt-3 block first:mt-0">
      <span className="mb-1 block text-[12px] font-medium text-body">{label}</span>
      {children}
      {hint ? <span className="mt-0.5 block text-[11px] text-faint">{hint}</span> : null}
    </label>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-4 rounded-lg border border-line bg-surface p-3">
      <h3 className="mb-2 text-[11px] font-bold tracking-wide text-faint uppercase">{title}</h3>
      {children}
    </section>
  );
}

/**
 * Rupees in the field, integer paise on the wire.
 *
 * Money is integer paise everywhere in this system (PLAN.md §3.1), and an
 * operator thinks in rupees. Converting at the edge — here, and nowhere else —
 * is what keeps a float from ever reaching a currency column: `Math.round`
 * runs on the way out, so 1299.99 becomes 129999 rather than 129998.999999998.
 */
export function toMinor(rupees: string): number | null {
  const value = Number(rupees);

  if (!rupees.trim() || !Number.isFinite(value) || value < 0) return null;

  return Math.round(value * 100);
}

export function fromMinor(minor: number): string {
  return (minor / 100).toFixed(2);
}
